import React, { useEffect, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { BlockUI } from 'primereact/blockui';
import { useNavigate } from 'react-router-dom';
import { useLocalState } from '../util/useLocalStorage';
import axios from 'axios';
import { BASE_URL } from '../services/ApiService/ApiConfig';
import { Message } from 'primereact/message';
import { CABECERA_CLIENTE, guardarIngreso, marcarGeocerca, marcarUbicacion, ubicacionNavegador } from '../seguridad/sesionCliente';
import ActivarSegundoFactor from '../seguridad/ActivarSegundoFactor';

const Login = () => {
    const navigate = useNavigate();

    const goDashboard = () => {
        navigate('/');
    };

    const [username, setUsername] = useLocalState("", "username");
    const [userId, setUserId] = useLocalState("", "userId");
    const [password, setPassword] = useState("");
    const [usuario, setUsuario] = useState("");
    const [roles, setRoles] = useLocalState("", "roles");
    const [blocked, setBlocked] = useState(false);
    const [jwt, setJwt] = useLocalState("", "jwt");

    useEffect(() => { 
        setRoles("");
    }, []);

    // Ingreso en pasos (ADR-002): clave -> (codigo del segundo factor | activarlo) -> datos del usuario.
    const [paso, setPaso] = useState('clave');
    const [desafio, setDesafio] = useState('');
    const [codigo, setCodigo] = useState('');
    const [error, setError] = useState('');

    async function pedirIngreso(url, cuerpo) {
        const res = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...CABECERA_CLIENTE },
            body: JSON.stringify(cuerpo)
        });
        const datos = await res.json().catch(() => ({}));
        if (!res.ok) {
            const e = new Error(datos.mensaje || 'Usuario o contrasena incorrectos');
            e.codigo = datos.codigo;
            throw e;
        }
        return datos;
    }

    async function sendLoginRequest(conUbicacion = false) {
        setBlocked(true);
        setError('');
        try {
            let cuerpo = { username, password };
            if (conUbicacion) {
                const u = await ubicacionNavegador();
                if (u) cuerpo = { ...cuerpo, ...u };
            }
            await continuar(await pedirIngreso(`${BASE_URL}/auth/authenticate`, cuerpo));
        } catch (e) {
            // la ubicacion solo se pide a quien la politica se la exige
            if (e.codigo === 'GEOCERCA' && !conUbicacion) return sendLoginRequest(true);
            console.error('Login Error:', e.message);
            setError(e.message || 'Usuario / Contrasena incorrecta o fallo de conexion.');
            setBlocked(false);
        }
    }

    async function continuar(r) {
        if (r.requiereSegundoFactor) {
            setDesafio(r.desafio);
            setCodigo('');
            setPaso('codigo');
            setBlocked(false);
            return;
        }
        guardarIngreso(r);
        setJwt(r.jwt);
        if (r.debeActivarSegundoFactor) {
            setPaso('activar');
            setBlocked(false);
            return;
        }
        await cargarUsuarioYEntrar(r.jwt);
    }

    async function enviarCodigo() {
        setBlocked(true);
        setError('');
        try {
            await continuar(await pedirIngreso(`${BASE_URL}/auth/segundo-factor`, { desafio, codigo }));
        } catch (e) {
            setError(e.message);
            if (e.codigo === 'DESAFIO') setPaso('clave');
            setBlocked(false);
        }
    }

    async function cargarUsuarioYEntrar(token) {
        setBlocked(true);
        try {
            const res = await fetch(`${BASE_URL}/usuario/` + username, {
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                method: 'get'
            });
            if (!res.ok) throw new Error('Error al obtener datos del usuario');
            const data = await res.json();
            setUserId(data.data.usuarioId);
            setUsuario(data);
            if (data.data.roles) setRoles(data.data.roles.map((r) => r.nombre));
            try {
                const mi = await fetch(`${BASE_URL}/seguridad/mi`, { headers: { Authorization: `Bearer ${token}` } });
                if (mi.ok) {
                    const m = await mi.json();
                    marcarGeocerca(m.geocerca);
                    marcarUbicacion(m.pedirUbicacion);
                }
            } catch (e) { /* la geocerca se vuelve a consultar en la proxima pantalla */ }
            setUsername(username);
            windowsLocationDashboard();
        } catch (e) {
            setError(e.message);
            setBlocked(false);
        }
    }

    function windowsLocationDashboard() {
        setBlocked(false);
        // Rol DOLE-ANDEN: no tiene Dashboard -> aterriza directo en Logistica.
        let esAnden = false;
        try { esAnden = (JSON.parse(localStorage.getItem('roles')) || []).includes('DOLE-ANDEN'); } catch (e) { esAnden = false; }
        let destino = null;
        try { destino = sessionStorage.getItem('opem.destino'); sessionStorage.removeItem('opem.destino'); } catch (e) { destino = null; }
        if (destino && destino.startsWith('/') && destino !== '/login' && !destino.startsWith('//')) {
            window.location.hash = '#' + destino; // la pantalla del enlace; el servidor valida el permiso
            return;
        }
        window.location.href = esAnden ? "/components/logistica" : "/";
    }

    const blockuiSpinning = (
        <React.Fragment>
            <ProgressSpinner />
        </React.Fragment>
    );

    return (
        <BlockUI blocked={blocked} template={blockuiSpinning}>
            <div className="pages-body login-page flex flex-column">
                <div className="topbar p-3 flex justify-content-between flex-row align-items-center">
                    <div className="topbar-left ml-3 flex">
                        <div className="logo">
                            <img src="assets/layout/images/Logo_Cema.png" alt="CEMA" />
                        </div>
                    </div>
                    <div className="topbar-right mr-3 flex">
                        <Button onClick={goDashboard} type="button" label="DASHBOARD" className="p-button-text p-button-plain topbar-button"></Button>
                    </div>
                </div>

                <div className="align-self-center mt-auto mb-auto">
                    <div className="pages-panel card flex flex-column">
                        <div className="pages-header px-3 py-1">
                            <h2>LOGIN</h2>
                        </div>

                        <h4>Welcome</h4>

                        <div className="pages-detail mb-6 px-6">Porfavor ingresa tu usuario y contraseña para usar el sistema</div>

                        {paso === 'codigo' && (
                            <div className="input-panel flex flex-column px-3 mb-6 gap-3">
                                <label htmlFor="codigo-2fa">Codigo de 6 digitos de su aplicacion autenticadora (o un codigo de recuperacion)</label>
                                <InputText id="codigo-2fa" value={codigo} autoFocus autoComplete="one-time-code"
                                    onChange={(e) => setCodigo(e.target.value.trim())}
                                    onKeyDown={(e) => { if (e.key === 'Enter' && codigo) enviarCodigo(); }} />
                                <Button className="login-button" label="Verificar" onClick={() => enviarCodigo()} disabled={!codigo}></Button>
                                <Button className="p-button-text" label="Volver" onClick={() => { setPaso('clave'); setError(''); }}></Button>
                                {error && <Message severity="error" text={error} />}
                            </div>
                        )}
                        {paso === 'activar' && (
                            <div className="flex flex-column px-3 mb-6">
                                <ActivarSegundoFactor obligatorio alTerminar={() => cargarUsuarioYEntrar(JSON.parse(localStorage.getItem('jwt')))} />
                            </div>
                        )}
                        {paso === 'clave' && (<>
                        <div className="input-panel flex flex-column px-3">
                            <div className="p-inputgroup">
                                <span className="p-inputgroup-addon">
                                    <i className="pi pi-envelope"></i>
                                </span>
                                <span className="p-float-label">
                                    <InputText type="text" id="inputgroup1" value={username} onChange={(e) => setUsername(e.target.value.toLowerCase().trim())} />
                                    <label htmlFor="inputgroup1">Email</label>
                                </span>
                            </div>

                            <div className="p-inputgroup mt-3 mb-6">
                                <span className="p-inputgroup-addon">
                                    <i className="pi pi-lock"></i>
                                </span>
                                <span className="p-float-label">
                                    <InputText type="password" id="inputgroup2" value={password} onChange={(e) => setPassword(e.target.value.toLowerCase().trim())}
                                        onKeyDown={(e) => { if (e.key === 'Enter') sendLoginRequest(); }} />
                                    <label htmlFor="inputgroup2">Password</label>
                                </span>
                            </div>
                        </div>

                        {error && <div className="px-3 mb-3"><Message severity="error" text={error} /></div>}
                        <Button className="login-button mb-6 px-3" label="Iniciar Sesion" onClick={() => sendLoginRequest()}></Button>
                        </>)}
                    </div>
                </div>
            </div>
        </BlockUI>
    );
};

export default Login;
